import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { format } from "date-fns";
import { Field, Form, Formik } from "formik";

import _get from "lodash/get";
import _isEqual from "lodash/isEqual";
import _omit from "lodash/omit";

import Table from "components/lib/Global/ReactTable";
import Button from "components/lib/Shared/Button.jsx";
import Checkbox from "components/lib/Shared/Checkbox";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import EmptyTableState from "components/lib/Shared/EmptyTableState";
import { ChevronLeftIcon } from "components/lib/Shared/Icons/sflIcons";
import Input from "components/lib/Shared/Input";
import ValidationError from "components/lib/Shared/ValidationError";
import { Constants } from "config/constants";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import { useToast } from "hooks/useToast";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { getFullName } from "utils";
import { getCompanySuffix } from "utils/company";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { isNameAbbreviated, trimCompanySuffix } from "./companyUtils";
import companyValidation from "./companyValidation";
import SpecialInstructions from "./SpecialInstructions";
import { handleFilesUpload } from "utils/filesUtils";

const STORAGE_KEY = (companyId) => `summaryForm_${companyId || "default"}`;

const CompanySummary = ({ handleSummaryNavigation }) => {
  const { company, dashboardRoles, companyOfficials } = useSelector(
    (state) => state.companyDashboardSlice
  );

  const { batchNo } = useSelector((state) => state.batchNumberSlice);

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const { companyId, trxId } = useParams();

  const {
    isInUpdateHistoryContext,
    selectionsMade,
    isStrikeOffSelected,
    setIsStrikeOffSelected,
    addUniqueTransactionId,
    transactionExists,
    setRefechTransactionsOnUpdate,
    refechTransactionsOnUpdate,
    createdTransactionsIds,
    transactions,
    dateFrom,
    dateTo,
  } = useContext(UpdateHistoryContext);

  const { transactionToEdit, isInManualTransactionEditContext } = useContext(
    ManualTransactionEditContext
  );

  const {
    isUpdateHistory,
    dateTo: To,
    dateFrom: From,
    historyTransactions,
    period,
  } = useSelector((state) => state.transferYourBusinessSlice);

  const dispatch = useDispatch();
  const { toast } = useToast(5000);

  const [companySuffix, setCompanySuffix] = useState(
    trimCompanySuffix(company?.registrationName)
  );

  const companyChanges = useMemo(
    () => selectionsMade?.summary?.companyChanges || [],
    [selectionsMade?.summary?.companyChanges]
  );

  const dateOfChangeRequired = companyChanges?.some((change) =>
    [
      "conversionPLCtoPVT",
      "conversionPVTtoPLC",
      "changeOfCompanyName",
      "strikeOffApplication",
    ].includes(change)
  );

  const {
    dateOfChangeOfFYRequired,
    cancellationDateRequired,
    strikeOffWithdrawalDateRequired,
    strikeOffApplicationTypeRequired,
    restorationDateRequired,
    isResistrationNameRequired,
  } = dateToShow(companyChanges);

  const isApplicantsRequired =
    companyChanges?.includes("noticeToCancelConversionPVTtoPLC") ||
    companyChanges?.includes("noticeToCancelConversionPLCtoPVT");

  const validationSchema = useMemo(
    () =>
      companyValidation({
        dateOfChangeRequired,
        dateOfChangeOfFYRequired,
        cancellationDateRequired,
        strikeOffWithdrawalDateRequired,
        strikeOffApplicationTypeRequired,
        restorationDateRequired,
        isResistrationNameRequired,
        isApplicantsRequired,
      }),
    [dateOfChangeRequired, dateOfChangeOfFYRequired, cancellationDateRequired]
  );

  const [isAbbreviated, setIsAbbreviated] = useState(
    companySuffix === "PLC" || companySuffix === "PVT"
  );
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState(company?.type ?? "");

  // Derived values
  const storageKey = useMemo(() => STORAGE_KEY(company?._id), [company?._id]);

  //trimCompanySuffix(company?.registrationName) ??
  // Initial values
  const initialValues = {
    registrationName: trimCompanySuffix(company?.registrationName) ?? "",
    registrationNumber: company?.registrationNumber ?? "",
    type: company?.type ?? "",
    strikeOffApplicationType: company?.strikeOffApplicationType ?? "",
    registrationDate: company?.registrationDate
      ? format(new Date(company.registrationDate), "yyyy-MM-dd")
      : "",
    natureOfBusiness: company?.articlesOfAssociation?.natureOfBusiness ?? "",
    accountingEndMonth:
      company?.primaryBusinessActivity?.accountingEndMonth ?? "",
    dateOfChange: "",
    dateOfChangeOfFY: "",
    specialInstructions: company?.specialInstructions ?? "",
    proposedName: company?.registrationName ?? "",
    applicants: [],
    selectedApplicantId: "",
    cancellationDate: "",
    strikeOffWithdrawalDate: "",
    restorationDate: "",
  };

  useEffect(() => {
    dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId: company?._id,
        params: {
          limit: null,
        },
      })
    );
  }, [createdTransactionsIds]);

  //Effects
  useEffect(() => {
    if (companyName === "") {
      setCompanySuffix("");
    } else {
      setCompanySuffix(` ${getCompanySuffix(isAbbreviated, companyType)}`);
    }
  }, [isAbbreviated, companyType]);

  useEffect(() => {
    if (transactionToEdit?.companyChangedFields?.registrationName) {
      setIsAbbreviated(
        isNameAbbreviated(
          transactionToEdit.companyChangedFields.registrationName
        )
      );
      setCompanyName(
        trimCompanySuffix(
          transactionToEdit.companyChangedFields.registrationName
        ) ?? ""
      );
    } else {
      setCompanyName(trimCompanySuffix(company?.registrationName) ?? "");
    }
  }, [transactionToEdit?.companyChangedFields, company?.registrationName]);

  const changeOfNameTrx = transactions?.find(
    (trx) =>
      trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
  );
  const changeOfFYTrx = transactions?.find(
    (trx) => trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
  );

  const conversionTrx = transactions?.find(
    (trx) => trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
  );

  useEffect(() => {
    if (changeOfNameTrx?.companyChangedFields?.registrationName) {
      setIsAbbreviated(
        isNameAbbreviated(changeOfNameTrx.companyChangedFields.registrationName)
      );
      setCompanyName(
        trimCompanySuffix(
          changeOfNameTrx.companyChangedFields.registrationName
        ) ?? ""
      );
    } else {
      setCompanyName(trimCompanySuffix(company?.registrationName) ?? "");
    }
  }, [changeOfNameTrx, company?.registrationName]);

  useEffect(() => {
    getInitialValues();
  }, [changeOfNameTrx, changeOfFYTrx, conversionTrx]);

  const getInitialValues = () => {
    const savedValues = localStorage?.getItem(storageKey);

    let values = {
      ...initialValues,
    };

    //Use the transactions to get the initial values
    if (
      (changeOfNameTrx?.companyChangedFields ||
        conversionTrx?.companyChangedFields) &&
      !refechTransactionsOnUpdate
    ) {
      values = {
        ...values,
        ..._omit(changeOfNameTrx?.companyChangedFields, [
          "primaryBusinessActivity",
        ]),
        registrationName:
          trimCompanySuffix(
            changeOfNameTrx.companyChangedFields.registrationName ??
              company?.registrationName
          ) ?? "",
        dateOfChange:
          changeOfNameTrx.companyChangedFields.dateOfChange ??
          initialValues?.dateOfChange,
        specialInstructions:
          changeOfNameTrx.companyChangedFields.specialInstructions ??
          initialValues?.specialInstructions,
      };
    }

    if (changeOfFYTrx?.companyChangedFields && !refechTransactionsOnUpdate) {
      values = {
        ...values,
        accountingEndMonth:
          changeOfFYTrx?.companyChangedFields?.primaryBusinessActivity
            ?.accountingEndMonth ?? initialValues?.accountingEndMonth,
        dateOfChangeOfFY:
          changeOfFYTrx?.companyChangedFields?.dateOfChangeOfFY ??
          initialValues?.dateOfChangeOfFY,
      };
    }

    //If in transaction edit mode, override with changed fields
    if (
      transactionToEdit?.companyChangedFields &&
      !(changeOfFYTrx || changeOfNameTrx) &&
      !refechTransactionsOnUpdate
    ) {
      values = {
        ...values,
        ..._omit(transactionToEdit.companyChangedFields, [
          "primaryBusinessActivity",
        ]),
        dateOfChange:
          transactionToEdit.companyChangedFields.dateOfChange ??
          initialValues?.dateOfChange,
      };
    }

    // If there are saved values in localStorage, merge them with initial values
    if (savedValues) {
      const parsedValues = JSON?.parse(savedValues) ?? {};
      return {
        ...parsedValues,
        ...values,
      };
    }
    return values;
  };

  useEffect(() => {
    const savedValues = localStorage?.getItem(storageKey);
    if (savedValues) {
      const parsedValues = JSON?.parse(savedValues);
      setIsAbbreviated(parsedValues?.isAbbreviated);
    }
  }, [storageKey]);

  // Get changed fields
  const getChangedFields = (values, initialValues) => {
    const changedFields = {};

    // Compare top-level fields
    Object.keys(values).forEach((key) => {
      if (!_isEqual(values[key], initialValues[key])) {
        if (key === "registrationDate") {
          changedFields[key] = new Date(values[key]).toISOString();
        } else if (key === "accountingEndMonth") {
          changedFields.primaryBusinessActivity = {
            accountingEndMonth: parseInt(values[key], 10),
          };
        } else if (key === "natureOfBusiness") {
          changedFields.articlesOfAssociation = {
            natureOfBusiness: values[key],
          };
        } else {
          changedFields[key] = values[key];
        }
      }
    });

    return changedFields;
  };
  const [submitting, setSubmitting] = useState(false);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Get changed fields

      const changedFieldsData = getChangedFields(values, initialValues);
      const hasConversionChange = (changes) => {
        return (
          changes &&
          (changes.includes("conversionPLCtoPVT") ||
            changes.includes("conversionPVTtoPLC"))
        );
      };

      if (Object.keys(changedFieldsData).length === 0) {
        toast("error", "You have not made changes.");
        return;
      }

      if (
        !changedFieldsData.registrationName &&
        companyChanges.includes("changeOfCompanyName") &&
        !changedFieldsData.proposedName
      ) {
        toast("error", "Change of Registration name is required.");
        return;
      }

      if (
        !changedFieldsData.registrationNumber &&
        hasConversionChange(companyChanges)
      ) {
        toast("error", "Change of Registration Number is required.");
        return;
      }

      if (!changedFieldsData.type && hasConversionChange(companyChanges)) {
        toast("error", "Change of Entity Type is required.");
        return;
      }

      if (
        !changedFieldsData?.primaryBusinessActivity?.accountingEndMonth &&
        companyChanges.includes("accountingReferenceDate")
      ) {
        toast(
          "error",
          "You have  not made changes to the accounting period end month."
        );
        return;
      }

      localStorage.setItem(storageKey, JSON.stringify(values));

      let transactionPayload = {};

      // Conversion transaction if conversionPLCtoPVT or conversionPVTtoPLC is in selectionsMade
      if (hasConversionChange(companyChanges)) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
        );

        if (!changedFieldsData?.type) {
          toast("error", "Change of Entity Type is required.");
          return;
        }
        if (!changedFieldsData?.registrationNumber) {
          toast("error", "Change of Registration Number is required.");
          return;
        }

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.CONVERSION) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              ...(transactionToEdit?.companyChangedFields ||
                existingTransaction?.companyChangedFields),
              registrationName: companyName + companySuffix,
              registrationNumber: changedFieldsData?.registrationNumber,
              affiliatedBusiness: values?.affiliatedBusiness,
              dateOfChange: changedFieldsData?.dateOfChange,
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload
          );
          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType: Constants.TRANSACTION_CHANGE_TYPES.CONVERSION,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              type: changedFieldsData?.type,
              registrationNumber: changedFieldsData?.registrationNumber,
              registrationName: companyName + companySuffix,
              affiliatedBusiness: values?.affiliatedBusiness,
              dateOfChange: changedFieldsData?.dateOfChange,
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            batchNo,
            approvalType:
              Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }

          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      // Name change transaction if changeOfCompanyName is in selectionsMade
      if (
        companyChanges?.includes("changeOfCompanyName") &&
        changedFieldsData.registrationName
      ) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
        );

        //handle affiliated business file upload
        let uploadedFile = {};
        if (
          values?.affiliatedBusiness &&
          (values.affiliatedBusiness.affiliatedBusinessName?.trim() ||
            values.affiliatedBusiness.affiliatedBusinessRegNumber?.trim()) &&
          values.affiliatedBusiness?.fileUploads?.affiliatedBusiness_LNO
        ) {
          const result = await handleFilesUpload(
            values.affiliatedBusiness.fileUploads
          );
          uploadedFile = result;

          if (result?.error) {
            toast(
              "error",
              "There was an error uploading the affiliated business file. Please try again."
            );
            return;
          }
        }

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              ...(transactionToEdit?.companyChangedFields ||
                existingTransaction?.companyChangedFields),
              registrationName: companyName + companySuffix,

              ...(values?.affiliatedBusiness &&
                (values.affiliatedBusiness.affiliatedBusinessName?.trim() ||
                  values.affiliatedBusiness.affiliatedBusinessRegNumber?.trim()) && {
                  affiliatedBusiness: {
                    ...values.affiliatedBusiness,
                    fileUploads: uploadedFile,
                  },
                  specialInstructions: values?.specialInstructions,
                }),
              dateOfChange: changedFieldsData?.dateOfChange,
              isCompanyNameAbbreviated: values?.isAbbreviated ? "Yes" : "No",
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );

          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType: Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              registrationName: companyName + companySuffix,
              ...(values?.affiliatedBusiness &&
                (values.affiliatedBusiness.affiliatedBusinessName?.trim() ||
                  values.affiliatedBusiness.affiliatedBusinessRegNumber?.trim()) && {
                  affiliatedBusiness: {
                    ...values.affiliatedBusiness,
                    fileUploads: uploadedFile,
                  },
                  specialInstructions: values?.specialInstructions,
                }),
              dateOfChange: changedFieldsData?.dateOfChange,
              isCompanyNameAbbreviated: values?.isAbbreviated ? "Yes" : "No",
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            batchNo,
            approvalType:
              Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );
          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }

          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (
        companyChanges?.includes("accountingReferenceDate") &&
        changedFieldsData?.primaryBusinessActivity?.accountingEndMonth
      ) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
        );

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              ...(transactionToEdit?.companyChangedFields ||
                existingTransaction?.companyChangedFields),
              primaryBusinessActivity: {
                accountingEndMonth:
                  changedFieldsData?.primaryBusinessActivity
                    ?.accountingEndMonth,
              },
              dateOfChangeOfFY: changedFieldsData?.dateOfChangeOfFY,
              effectiveFromDate: changedFieldsData?.dateOfChangeOfFY,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );

          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType: Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              primaryBusinessActivity: {
                accountingEndMonth:
                  changedFieldsData?.primaryBusinessActivity
                    ?.accountingEndMonth,
              },
              dateOfChangeOfFY: changedFieldsData?.dateOfChangeOfFY,
              effectiveFromDate: changedFieldsData?.dateOfChangeOfFY,
            },
            batchNo,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }

          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (companyChanges.includes("strikeOffApplication")) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
        );

        const strikeOffApplicationType =
          changedFieldsData?.strikeOffApplicationType;

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              ...(transactionToEdit?.companyChangedFields ||
                existingTransaction?.companyChangedFields),
              strikeOffApplicationType,
              dateOfChange: changedFieldsData?.dateOfChange,
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            approvalType:
              strikeOffApplicationType ===
              Constants.ENTITY_STRIKE_APPLICATION_OPTIONS.COURT_LIQUIDATION
                ? Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL
                : strikeOffApplicationType ===
                  Constants.ENTITY_STRIKE_APPLICATION_OPTIONS
                    .CREDITORS_LIQUIDATION
                ? Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL
                : Constants.TRANSACTION_APPROVAL_TYPES
                    .BOARD_SHAREHOLDER_APPROVAL,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );

          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType: Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              strikeOffApplicationType,
              dateOfChange: changedFieldsData?.dateOfChange,
              effectiveFromDate: changedFieldsData?.dateOfChange,
            },
            batchNo,
            approvalType:
              strikeOffApplicationType ===
              Constants.ENTITY_STRIKE_APPLICATION_OPTIONS.COURT_LIQUIDATION
                ? Constants.TRANSACTION_APPROVAL_TYPES.BOARD_COURT_APPROVAL
                : strikeOffApplicationType ===
                  Constants.ENTITY_STRIKE_APPLICATION_OPTIONS
                    .CREDITORS_LIQUIDATION
                ? Constants.TRANSACTION_APPROVAL_TYPES.BOARD_CREDITORS_APPROVAL
                : Constants.TRANSACTION_APPROVAL_TYPES
                    .BOARD_SHAREHOLDER_APPROVAL,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }

          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }
          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (
        companyChanges.includes("noticeToCancelConversionPVTtoPLC") ||
        companyChanges.includes("noticeToCancelConversionPLCtoPVT")
      ) {
        //TODO: Check if total shareholding percentage is greater than or equal to 5%
        let shareholdingPercentage = 0;

        for (const applicant of changedFieldsData?.applicants) {
          shareholdingPercentage += Number(applicant?.sharePercentage);
        }

        if (shareholdingPercentage < 5) {
          toast(
            "error",
            "Total shareholding percentage should be greater than or equal to 5%."
          );
          return;
        }

        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
        );

        const conversionTransactionToCancel =
          initiatedTransactions?.items?.find((item) => {
            const { companyChangedFields, status } = item;

            const dateOfChange = companyChangedFields?.dateOfChange;

            if (!dateOfChange) return false;

            const changeDate = new Date(dateOfChange);

            return (
              item?.status &&
              item?.status ===
                Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT &&
              item?.changeType ===
                Constants.TRANSACTION_CHANGE_TYPES.CONVERSION &&
              item?.isForHistory === true &&
              changeDate >= new Date(dateFrom) &&
              changeDate <= new Date(dateTo)
            );
          });

        if (!conversionTransactionToCancel) {
          toast("error", "There was no conversion transaction to cancel.");
          return;
        }

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              applicants: changedFieldsData?.applicants,
              cancellationDate: changedFieldsData?.cancellationDate,
              transactionToCancelDetails: {
                _id: conversionTransactionToCancel?._id,
                status: conversionTransactionToCancel?.status,
              },
              effectiveFromDate: changedFieldsData?.cancellationDate,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };
          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );
          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType:
              Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              applicants: changedFieldsData?.applicants,
              cancellationDate: changedFieldsData?.cancellationDate,
              transactionToCancelDetails: {
                _id: conversionTransactionToCancel?._id,
                status: conversionTransactionToCancel?.status,
              },
              effectiveFromDate: changedFieldsData?.cancellationDate,
            },
            batchNo,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );
          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }
          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (
        companyChanges?.includes("noticeOfWithdrawalOfStrikeOffApplication")
      ) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF
        );

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES
                .NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            companyChangedFields: {
              strikeOffWithdrawalDate:
                changedFieldsData?.strikeOffWithdrawalDate,
              effectiveFromDate: changedFieldsData?.strikeOffWithdrawalDate,
            },
            documentId: transactionToEdit?._id || existingTransaction?._id,
            batchNo,
            period,
          };
          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );
          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType:
              Constants.TRANSACTION_CHANGE_TYPES
                .NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            approvalType:
              Constants.TRANSACTION_APPROVAL_TYPES.SHAREHOLDER_APPROVAL,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyId,
            companyChangedFields: {
              strikeOffWithdrawalDate:
                changedFieldsData?.strikeOffWithdrawalDate,
              effectiveFromDate: changedFieldsData?.strikeOffWithdrawalDate,
            },
            batchNo,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );

          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }
          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      if (companyChanges?.includes("applicationForAdministrativeRestoration")) {
        const existingTransaction = transactionExists(
          Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
        );

        let transactionPayload = {};

        if (
          (isInManualTransactionEditContext &&
            transactionToEdit?.changeType ===
              Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION) ||
          existingTransaction
        ) {
          transactionPayload = {
            companyId,
            documentId: transactionToEdit?._id || existingTransaction?._id,
            companyChangedFields: {
              restorationDate: changedFieldsData?.restorationDate,
              effectiveFromDate: changedFieldsData?.restorationDate,
            },
            batchNo,
            period,
          };
          const result = await handleCompanyMaintenanceFormData(
            "existing-request",
            transactionPayload,
            companyId
          );
          if (result?.error) {
            toast(
              "error",
              "There was an error updating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been updated successfully");
            setRefechTransactionsOnUpdate(true);
          }
        } else {
          transactionPayload = {
            changeType:
              Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION,
            applicationType:
              Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
            approvalType:
              Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL,
            transactionType: Constants.TRANSACTION_TYPES.MANUAL,
            companyChangedFields: {
              restorationDate: changedFieldsData?.restorationDate,
              effectiveFromDate: changedFieldsData?.restorationDate,
            },
            companyId,
            batchNo,
            isForHistory: true,
            period,
          };

          const result = await handleCompanyMaintenanceFormData(
            "new-request",
            transactionPayload,
            companyId
          );
          if (result?.payload?.errors) {
            toast(
              "error",
              "There was an error creating the transaction. Please try again."
            );
            return;
          }
          if (result?.payload?.status === true) {
            toast("success", "Transaction has been created successfully");
            setRefechTransactionsOnUpdate(true);
          }
          addUniqueTransactionId(result?.payload?.savedTransaction?._id);
        }
      }

      handleSummaryNavigation("next"); // Proceed after submission
      setSubmitting(false);
    } catch (error) {
      console.error("Failed to create transaction:", error);
      // Optionally handle error (e.g., show error message)
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    localStorage.removeItem(storageKey);
  }, []);

  // Field configuration
  const fields = useMemo(
    () => [
      [
        {
          key: "Registration Name",
          name: "registrationName",
          value: _get(company, "registrationName", "N/A")
            .split(" ")
            .slice(0, -1)
            .join(" "),
          type: "text",
          isEditable:
            companyChanges?.includes("changeOfCompanyName") &&
            !isStrikeOffSelected,
          showField: companyChanges?.some((change) =>
            [
              "changeOfCompanyName",
              "conversionPLCtoPVT",
              "conversionPVTtoPLC",
            ].includes(change)
          ),
          showAbbreviationCheckbox: true,
          proposedName: true,
          showSpecialInstructions: true,
        },
        {
          key: "Registration Number",
          name: "registrationNumber",
          value: _get(company, "registrationNumber", "N/A"),
          type: "text",
          isEditable:
            (companyChanges?.includes("conversionPLCtoPVT") ||
              companyChanges?.includes("conversionPVTtoPLC")) &&
            !isStrikeOffSelected,
          showField: companyChanges?.some((change) =>
            ["conversionPLCtoPVT", "conversionPVTtoPLC"].includes(change)
          ),
        },
        {
          key: "Entity Type",
          name: "type",
          isEditable:
            (companyChanges?.includes("conversionPLCtoPVT") ||
              companyChanges?.includes("conversionPVTtoPLC")) &&
            !isStrikeOffSelected,
          value:
            Constants.ENTITY_TYPE_AGAINST_TYPE[_get(company, "type", "N/A")] ||
            "N/A",
          type: "select",
          options: Object.entries(Constants.ENTITY_TYPE_AGAINST_TYPE).map(
            ([value, label]) => ({ value, label })
          ),
          showField: companyChanges?.some((change) =>
            [, "conversionPLCtoPVT", "conversionPVTtoPLC"].includes(change)
          ),
        },
      ],
      [
        {
          key: "Strike Off Application Type",
          name: "strikeOffApplicationType",
          type: "select",
          isEditable: companyChanges?.includes("strikeOffApplication"),
          options: Object.entries(
            Constants.ENTITY_STRIKE_APPLICATION_OPTIONS
          ).map(([, label]) => ({
            value: label,
            label,
          })),
        },
      ],
      [
        {
          key: "Annual Return Date",
          name: "registrationDate",
          value: company?.registrationDate
            ? `${format(
                new Date(company?.registrationDate),
                "dd/MM/yyyy"
              ).slice(0, -2)}**`
            : "N/A",
          type: "text",
          disabled: true,
          isEditable: false,
        },
        {
          key: "Financial YE",
          name: "accountingEndMonth",
          isEditable:
            companyChanges?.includes("accountingReferenceDate") &&
            !isStrikeOffSelected,
          value: "",
          type: "select",
          options: Array.from({ length: 12 }, (_, i) => ({
            value: i + 1,
            label: format(new Date(2025, i, 1), "MMMM"),
          })),
        },
      ],
    ],
    [company, companyChanges, isStrikeOffSelected]
  );

  const shareHolders = companyOfficials?.items?.filter((official) =>
    [
      Constants.APPLICANT_CAPACITIES.SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER,
      Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
    ].includes(official?.designation)
  );

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, values, setFieldValue }) => {
        useEffect(() => {
          setCompanyType(values?.type);
          if (values?.type === "PLC") {
            setIsAbbreviated(true);
          }
        }, [values?.type]);

        useEffect(() => {
          setFieldValue("proposedName", companyName + companySuffix);
          localStorage.setItem(storageKey, JSON?.stringify(values));
        }, [values, companyName, companySuffix]);

        setCompanyName(values?.registrationName);
        const handleChecked = (e) => {
          const { name, checked } = e.target;
          setIsAbbreviated(checked);
          setFieldValue(name, checked);
        };

        const handleDateOfChange = (date) => {
          if (!date) {
            setFieldValue("dateOfChange", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("dateOfChange", formattedDate);
        };

        const handleChangeOfFYDate = (date) => {
          if (!date) {
            setFieldValue("dateOfChangeOfFY", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("dateOfChangeOfFY", formattedDate);
        };

        const handleChangeOfCancellationDate = (date) => {
          if (!date) {
            setFieldValue("cancellationDate", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("cancellationDate", formattedDate);
        };

        const handleChangeOfWithdrawalDate = (date) => {
          if (!date) {
            setFieldValue("strikeOffWithdrawalDate", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("strikeOffWithdrawalDate", formattedDate);
        };

        const handleChangeOfRestorationDate = (date) => {
          if (!date) {
            setFieldValue("restorationDate", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("restorationDate", formattedDate);
        };

        const resolvedFields = transactionToEdit?.resolvedFields || [];
        // Columns definition for the Table component
        const columns = useMemo(
          () => [
            {
              Header: "Name",
              accessor: "name",
            },
            {
              Header: "Designation",
              accessor: "designation",
            },
            {
              Header: "Shareholding (%)",
              accessor: "sharePercentage",
              Cell: ({ row }) => (
                <span
                  className={row.original.id === "total" ? "font-semibold" : ""}
                >
                  {row.original.sharePercentage}%
                </span>
              ),
              className: "whitespace-nowrap",
            },

            {
              Header: "Action",
              Cell: ({ row }) => {
                const index = row.index;
                return (
                  <button
                    type="button"
                    onClick={() => {
                      setFieldValue(
                        "applicants",
                        values?.applicants.filter((_, i) => i !== index)
                      );
                    }}
                    className={`disabled:cursor-not-allowed disabled:text-gray-400 text-red-500 hover:text-red-700`}
                    disabled={resolvedFields.length > 0}
                  >
                    Remove
                  </button>
                );
              },
            },
          ],
          [setFieldValue, values?.applicants]
        );

        // Table data with total row
        const tableData = useMemo(() => {
          const applicants = values?.applicants || [];

          return [...applicants];
        }, [values?.applicants]);

        useEffect(() => {
          if (!values?.cancellationDate) {
            setFieldValue("applicants", []);
          }
        }, [values?.cancellationDate]);

        const allApplicants = useMemo(() => {
          const filteredApplicants = shareHolders?.filter((official) => {
            return (
              new Date(official?.appointmentDate) <=
              new Date(values?.cancellationDate)
            );
          });

          return filteredApplicants?.map((official) => ({
            id: official._id,
            name: getFullName(official.names),
            designation: official?.designation,
            percentage:
              official?.beneficialOwnershipDetails[0]?.maxPercentShareholding,
          }));
        }, [values?.cancellationDate]);

        return (
          <Form className="divide-y-2">
            {/* Save to localStorage on form change */}
            {localStorage.setItem(storageKey, JSON.stringify(values))}

            {/* Group 1: Registration Name, Registration Number, Entity Type */}
            <div
              className={`${
                companyChanges.some((change) =>
                  [
                    "changeOfCompanyName",
                    "conversionPVTtoPLC",
                    "conversionPLCtoPVT",
                  ].includes(change)
                )
                  ? "px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4"
                  : ""
              }`}
            >
              <div className="flex flex-col space-y-2">
                {fields[0].map((field, index) =>
                  field.showField ? (
                    <div
                      key={`${field.key}_${index}`}
                      className="flex flex-col"
                    >
                      <div className="flex items-baseline justify-between">
                        <label
                          htmlFor={field.name}
                          className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                        >
                          {field.key}
                        </label>

                        <div className="flex flex-col w-full space-y-2">
                          {field.proposedName &&
                          Array.isArray(companyChanges) &&
                          companyChanges.some((change) =>
                            [
                              "changeOfCompanyName",
                              "conversionPVTtoPLC",
                              "conversionPLCtoPVT",
                            ].includes(change)
                          ) ? (
                            <div className="block text-sm font-medium text-gray-700">
                              <span className="after:content-['*'] after:ml-0.5 after:text-red-500">
                                Proposed Name
                              </span>
                              <p>
                                {companyName
                                  ? companyName + companySuffix
                                  : null}
                              </p>
                            </div>
                          ) : null}

                          {field.isEditable ? (
                            <div
                              className={`bg-formInput block w-full text-sm rounded-md`}
                            >
                              {field.type === "select" ? (
                                <Field
                                  as="select"
                                  name={field.name}
                                  disabled={field.disabled}
                                  className="w-full bg-transparent text-primary focus:outline-none rounded-md border-gray-300"
                                >
                                  {field.options?.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                  <ValidationError
                                    errors={errors}
                                    touched={touched}
                                    name={field.name}
                                  />
                                </Field>
                              ) : (
                                <Field
                                  name={field.name}
                                  id={field.name}
                                  placeholder={`Add ${field.key}`}
                                  component={Input}
                                  onChange={(e) => {
                                    setFieldValue(field.name, e.target.value);
                                    // field.name === "registrationName" &&
                                    //   setCompanyName(e.target.value);
                                  }}
                                  disabled={field.disabled}
                                  className="w-full bg-transparent text-primary placeholder-primary focus:outline-none mt-0"
                                />
                              )}
                            </div>
                          ) : (
                            <div
                              className={`bg-formInput flex w-[80%] justify-between relative`}
                            >
                              {field.value}
                            </div>
                          )}
                          {field?.showAbbreviationCheckbox &&
                            Array.isArray(companyChanges) &&
                            companyChanges.some((change) =>
                              [
                                "changeOfCompanyName",
                                "conversionPVTtoPLC",
                                "conversionPLCtoPVT",
                              ].includes(change)
                            ) && (
                              <Checkbox
                                name="isAbbreviated"
                                label="Abbreviation (Shorten name suffix to initials)"
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none disabled:cursor-not-allowed"
                                onChange={handleChecked}
                                checked={isAbbreviated}
                              />
                            )}

                          {field?.showAbbreviationCheckbox &&
                            Array.isArray(companyChanges) &&
                            companyChanges.some((change) =>
                              [
                                "changeOfCompanyName",
                                "conversionPVTtoPLC",
                                "conversionPLCtoPVT",
                              ].includes(change)
                            ) && (
                              <Field
                                component={SpecialInstructions}
                                name="specialInstructions"
                              />
                            )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                )}
              </div>
              {companyChanges.some((change) =>
                [
                  "changeOfCompanyName",
                  "conversionPVTtoPLC",
                  "conversionPLCtoPVT",
                ].includes(change)
              ) && (
                <div className="p-2 mt-4">
                  <DatePickerField
                    label="Effective date of change"
                    onChange={handleDateOfChange}
                    selected={
                      values?.dateOfChange
                        ? new Date(values?.dateOfChange)
                        : null
                    }
                    name="dateOfChange"
                    placeholderText="Enter date of change"
                    maxDate={dateTo}
                    minDate={dateFrom}
                    errorPath="dateOfChange"
                    disabled={false}
                    showRequiredAsterisk
                  />
                  <ValidationError
                    errors={errors}
                    touched={touched}
                    name="dateOfChange"
                  />
                </div>
              )}
            </div>

            {/* Group 2: Strike off application type*/}

            {fields[1].map((field, index) =>
              field.isEditable ? (
                <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                  <div className="flex flex-col space-y-2">
                    <div
                      key={`${field.key}_${index}`}
                      className="flex flex-col"
                    >
                      <div className="flex items-baseline justify-between">
                        <label
                          htmlFor={field.name}
                          className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                        >
                          {field.key}
                        </label>
                        <div
                          className={`bg-formInput block w-full p-2 text-sm rounded-md`}
                        >
                          {field.isEditable ? (
                            <Field
                              name={field.name}
                              component={ReactSelectWithFormik}
                              options={field.options}
                              errorPath={field.name}
                              placeholder={`Select ${field.key}`}
                              onBlur={field.onBlur}
                              onChange={(option) => {
                                setFieldValue(field.name, option.value);
                              }}
                              isDisabled={field.disabled}
                            />
                          ) : (
                            <div
                              className={`bg-formInput flex w-[80%] justify-between relative`}
                            >
                              {field.value}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 mt-4">
                    <DatePickerField
                      label="Effective date of change of entity status"
                      onChange={handleDateOfChange}
                      selected={
                        values?.dateOfChange
                          ? new Date(values?.dateOfChange)
                          : null
                      }
                      name="dateOfChange"
                      maxDate={dateTo}
                      minDate={dateFrom}
                      placeholderText="Enter date of change"
                      errorPath="dateOfChange"
                      disabled={false}
                      showRequiredAsterisk
                    />
                    <ValidationError
                      errors={errors}
                      touched={touched}
                      name="dateOfChange"
                    />
                  </div>
                </div>
              ) : (
                ""
              )
            )}

            {/* Group 3: Annual Return Date, Financial YE */}

            {fields[2].map((field, index) =>
              field.isEditable ? (
                <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                  <div className="flex justify-between">
                    <div
                      key={`${field.key}_${index}`}
                      className="flex flex-col items-baseline justify-between w-1/2"
                    >
                      <label
                        htmlFor={field.name}
                        className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                      >
                        {field.key}
                      </label>
                      {field.disabled || !field.isEditable ? (
                        <div
                          className={`bg-formInput flex w-[80%] justify-between relative`}
                        >
                          {field.value}
                        </div>
                      ) : (
                        <div className="w-full p-2 text-sm rounded-md bg-formInput">
                          {field.type === "select" ? (
                            <Field
                              name={field.name}
                              component={ReactSelectWithFormik}
                              options={field.options}
                              errorPath={field.name}
                              placeholder={`Select ${field.key}`}
                              onBlur={field.onBlur}
                              onChange={(option) => {
                                setFieldValue(field.name, option.value);
                              }}
                              isDisabled={field.disabled}
                            />
                          ) : (
                            <Field
                              name={field.name}
                              id={field.name}
                              placeholder={`Add ${field.key}`}
                              component={Input}
                              disabled={field.disabled}
                              className="w-full bg-transparent text-primary placeholder-primary focus:outline-none"
                              type={field.type}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-2 mt-4">
                    <DatePickerField
                      label="Effective date of change of Financial Year"
                      onChange={handleChangeOfFYDate}
                      selected={
                        values?.dateOfChangeOfFY
                          ? new Date(values?.dateOfChangeOfFY)
                          : null
                      }
                      name="dateOfChangeOfFY"
                      minDate={dateFrom}
                      maxDate={dateTo}
                      placeholderText="Enter date of change"
                      errorPath="dateOfChangeOfFY"
                      disabled={false}
                      showRequiredAsterisk
                    />
                    <ValidationError
                      errors={errors}
                      touched={touched}
                      name="dateOfChangeOfFY"
                    />
                  </div>
                </div>
              ) : (
                ""
              )
            )}

            {companyChanges?.some((change) =>
              [
                "noticeToCancelConversionPVTtoPLC",
                "noticeToCancelConversionPLCtoPVT",
              ].includes(change)
            ) && (
              <div>
                <div className="px-4 py-6 bg-gray-100 rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                  <p className="font-medium mt-4">
                    Notice to Cancel Company Conversion
                  </p>

                  {/* add note to tell users to select the date first before adding applicants */}
                  {!values?.cancellationDate && cancellationDateRequired && (
                    <p className="text-sm text-red-500 mt-2">
                      Please select the date of cancellation first before adding
                      applicants.
                    </p>
                  )}

                  {values?.cancellationDate &&
                    cancellationDateRequired &&
                    allApplicants?.length === 0 && (
                      <p className="text-sm text-red-500 mt-2">
                        Appoint at least one shareholder as at the date of
                        cancellation you selected or change the date to get the
                        shareholder(s) as at the date of cancellation.
                      </p>
                    )}

                  {/* Select button to add applicants */}
                  <div className="mt-4">
                    <Field
                      as="select"
                      name="selectedApplicantId"
                      className="w-full p-2 mt-1 border rounded-md"
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        if (selectedId) {
                          // Find the applicant from allApplicants
                          const selectedApplicant = allApplicants.find(
                            (applicant) => applicant.id === selectedId
                          );
                          if (selectedApplicant) {
                            // Check if already added
                            const alreadyAdded = values?.applicants?.some(
                              (app) => app.id === selectedId
                            );

                            if (!alreadyAdded) {
                              // Add to applicants array
                              const newApplicant = {
                                id: selectedApplicant.id,
                                name: selectedApplicant.name,
                                designation: selectedApplicant.designation,
                                sharePercentage:
                                  selectedApplicant.percentage || 0,
                              };

                              setFieldValue("applicants", [
                                ...(values.applicants || []),
                                newApplicant,
                              ]);
                            }

                            // Reset selection
                            setFieldValue("selectedApplicantId", "");
                          }
                        }
                      }}
                      disabled={
                        resolvedFields.length > 0 ||
                        !values?.cancellationDate ||
                        (values?.cancellationDate &&
                          cancellationDateRequired &&
                          allApplicants?.length === 0)
                      }
                    >
                      <option value="">Add Applicant(s)</option>
                      {allApplicants
                        ?.filter(
                          (applicant) =>
                            // Show only applicants not already selected
                            !values.applicants?.some(
                              (app) => app.id === applicant.id
                            )
                        )
                        .map((applicant) => (
                          <option key={applicant.id} value={applicant.id}>
                            {applicant.name} - {applicant.percentage}%
                          </option>
                        ))}
                    </Field>
                    <ValidationError
                      errors={errors}
                      touched={touched}
                      name="applicants"
                    />
                  </div>

                  {values?.applicants?.length > 0 ? (
                    <div className="mt-6 w-full bg-white shadow overflow-x-auto">
                      <Table
                        columns={columns}
                        data={tableData}
                        dataRowCellStyle="pl-4 leading-snug py-2"
                        paginate={true}
                        totalRows={tableData.length}
                      />
                      <div className="text-sm font-semibold text-gray-700 px-4 py-3 mt-2 bg-white border-t border-gray-300 sm:px-6">
                        Total Shareholding:{" "}
                        <span className="text-primary ml-5">
                          {values?.applicants
                            ?.reduce(
                              (sum, applicant) =>
                                sum + (applicant?.sharePercentage || 0),
                              0
                            )
                            .toFixed(2)}
                          %
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <EmptyTableState>No applicants added.</EmptyTableState>
                    </div>
                  )}
                </div>
                <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                  <DatePickerField
                    label="Effective date of cancellation of company conversion"
                    onChange={handleChangeOfCancellationDate}
                    selected={
                      values.cancellationDate
                        ? new Date(values?.cancellationDate)
                        : null
                    }
                    name="cancellationDate"
                    minDate={dateFrom}
                    maxDate={dateTo}
                    placeholderText="Enter date of cancellation"
                    errorPath="cancellationDate"
                    disabled={resolvedFields.length > 0}
                    showRequiredAsterisk
                  />
                  <ValidationError
                    errors={errors}
                    touched={touched}
                    name="cancellationDate"
                  />
                </div>
              </div>
            )}

            {companyChanges?.some((change) =>
              ["noticeOfWithdrawalOfStrikeOffApplication"].includes(change)
            ) && (
              <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                <p className="font-medium mt-4">
                  Notice of withdrawal of strike-off application
                </p>

                {/* Warning note */}
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-orange-400 mt-0.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-orange-800">
                        This transaction will reverse strike off transaction
                        ongoing.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <DatePickerField
                    label="Date of withdrawal of strike-off application"
                    onChange={handleChangeOfWithdrawalDate}
                    selected={
                      values?.strikeOffWithdrawalDate
                        ? new Date(values?.strikeOffWithdrawalDate)
                        : null
                    }
                    name="strikeOffWithdrawalDate"
                    minDate={dateFrom}
                    maxDate={dateTo}
                    placeholderText="Enter date of strike-off withdrawal"
                    errorPath="strikeOffWithdrawalDate"
                    disabled={false}
                    showRequiredAsterisk
                  />
                  <ValidationError
                    errors={errors}
                    touched={touched}
                    name="strikeOffWithdrawalDate"
                  />
                </div>
              </div>
            )}

            {companyChanges?.some((change) =>
              ["applicationForAdministrativeRestoration"].includes(change)
            ) && (
              <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 my-4">
                <p className="font-medium mt-4">
                  Application for administrative restoration
                </p>

                {/* Warning note */}
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-orange-400 mt-0.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-orange-800">
                        This transaction will restore the company from struck
                        off to active.
                      </p>
                      <p className="text-sm font-medium text-orange-800">
                        You have to file all due annual returns.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <DatePickerField
                    label="Date of company restoration"
                    onChange={handleChangeOfRestorationDate}
                    selected={
                      values.restorationDate
                        ? new Date(values?.restorationDate)
                        : null
                    }
                    name="restorationDate"
                    minDate={dateFrom}
                    maxDate={dateTo}
                    placeholderText="Enter date of restoration"
                    errorPath="restorationDate"
                    disabled={false}
                    showRequiredAsterisk
                  />
                  <ValidationError
                    errors={errors}
                    touched={touched}
                    name="restorationDate"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="px-4 py-3 bg-white sm:px-6">
              <div className="flex justify-between space-x-2">
                <button
                  type="button"
                  onClick={() => handleSummaryNavigation("prev")}
                  className="flex items-start float-right p-2 font-semibold rounded text-primary hover:bg-gray-700 hover:text-white"
                >
                  <span className="flex items-center mr-4 h-7">
                    <ChevronLeftIcon
                      className="flex-shrink-0 w-5 h-5"
                      aria-hidden="true"
                    />
                  </span>
                  <p className="font-semibold">Go back</p>
                </button>
                <Button
                  type="submit"
                  isLoading={submitting}
                  disabled={submitting}
                  loadingText="Saving..."
                  className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
                >
                  {submitting ? "Saving..." : "Save and proceed"}
                </Button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CompanySummary;
function dateToShow(companyChanges) {
  const dateOfChangeOfFYRequired = companyChanges?.includes(
    "accountingReferenceDate"
  );

  const cancellationDateRequired = companyChanges?.includes(
    "noticeToCancelConversionPVTtoPLC"
  );

  const strikeOffWithdrawalDateRequired = companyChanges?.includes(
    "noticeOfWithdrawalOfStrikeOffApplication"
  );
  const strikeOffApplicationTypeRequired = companyChanges?.includes(
    "strikeOffApplication"
  );

  const restorationDateRequired = companyChanges?.includes(
    "applicationForAdministrativeRestoration"
  );

  const isResistrationNameRequired = companyChanges?.includes(
    "changeOfCompanyName"
  );
  return {
    dateOfChangeOfFYRequired,
    cancellationDateRequired,
    strikeOffWithdrawalDateRequired,
    strikeOffApplicationTypeRequired,
    restorationDateRequired,
    isResistrationNameRequired,
  };
}
